// Story 1.19 — surfaces when an active (`pending`) deletion request
// exists. Shows the human-formatted scheduled_for, a live countdown,
// a primary "Cancel deletion" button (primary because the user is
// recovering from a mistake — cancellation is the action we want easy,
// per Cross-Cutting Pattern Rules), and the support-email escape hatch.

import { useEffect, useState } from "react";
import { Button } from "@greenhold/components";
import toast from "react-hot-toast";

import type { DeletionRequestStatus } from "./useAccountDeletionStatus";
import { useCancelDeletion } from "./useCancelDeletion";

const SUPPORT_EMAIL = "support@greenhold.world";

function humanizeRemaining(scheduledFor: Date, now: Date): string {
  const totalMs = scheduledFor.getTime() - now.getTime();
  if (totalMs <= 0) return "Less than a minute remaining";
  const totalMinutes = Math.floor(totalMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} remaining`;
  }
  return `${hours} hour${hours === 1 ? "" : "s"}, ${minutes} minute${
    minutes === 1 ? "" : "s"
  } remaining`;
}

type PendingDeletionPanelProps = {
  status: DeletionRequestStatus;
};

export function PendingDeletionPanel({ status }: PendingDeletionPanelProps) {
  const [now, setNow] = useState<Date>(() => new Date());
  const cancel = useCancelDeletion();

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const scheduledFor = new Date(status.scheduled_for);
  const remainingLabel = humanizeRemaining(scheduledFor, now);

  const handleCancel = async () => {
    try {
      await cancel.mutateAsync();
      toast.success("Deletion cancelled. Your account is back to normal.");
    } catch (err) {
      const detail = err as { code?: string; message?: string };
      if (detail.code === "ACCOUNT_DELETION_TOO_LATE") {
        toast.error(
          `Deletion is already being processed. Email ${SUPPORT_EMAIL} if you need help.`,
        );
        return;
      }
      toast.error(detail.message ?? "Cancellation failed. Try again.");
    }
  };

  return (
    <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 space-y-4">
      <header>
        <h2 className="font-heading text-lg font-semibold">
          Your account is scheduled for deletion
        </h2>
        <p className="font-body text-sm text-muted-foreground">
          Final deletion runs at{" "}
          <span className="font-semibold">{scheduledFor.toLocaleString()}</span>
          .
        </p>
      </header>

      <p className="font-body text-sm">
        <span aria-live="polite" className="font-semibold">
          {remainingLabel}
        </span>{" "}
        — you can cancel from this page until then.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="primary"
          onClick={handleCancel}
          disabled={cancel.isPending}
        >
          {cancel.isPending ? "Cancelling…" : "Cancel deletion"}
        </Button>
        <span className="text-xs text-muted-foreground">
          After the window closes, email{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="underline decoration-dotted"
          >
            {SUPPORT_EMAIL}
          </a>{" "}
          and we'll see what we can do.
        </span>
      </div>
    </div>
  );
}
